import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import { LineChart, CombinedBarLineChart } from '../../../../../shared/components/charts';
import { DashboardCard } from '../../../../../shared/components/DashboardCard';
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
  ComparisonToggle,
  GranularityToggle,
  ComparisonColumns,
} from '../../../../../shared/components';
import { useOverallStressTrends, useOverallStressPatterns } from '../hooks';
import { StressStatsColumn } from '../components/StressStatsColumn';
import { buildFilterParams, transformTrendsData, useResolvedGranularity } from '../../shared/utils';
import { useClientScope } from '../../../../../shared/contexts';
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
  useComparisonMode,
  useComparisonGroups,
  useComparisonTrends,
  useComparisonPatterns,
} from '../../../../../shared/hooks';
import { AiSummaryPanel, useAiSummary } from '../../../insights';
import { buildComparisonTrendSeries, buildComparisonPatternSeries } from '../../../../../shared/utils';

const OVERALL_STRESS_AND_MIND_DETAILS = {
  stressAndRecoveryTrends: `Displays daily variation in stress and recovery metrics over time. Stress reflects physiological load and sympathetic activation, while recovery indicates autonomic balance and restorative capacity.

Tracking these trends helps identify patterns of sustained stress, periods of effective recovery, and overall shifts in wellbeing across the organisation.`,
  stressLevelDistribution: `Displays the proportion of employees within high, medium, and low stress zones, providing a snapshot of overall stress burden across the organisation. Stress levels are typically derived from physiological indicators such as heart rate patterns and heart rate variability, reflecting autonomic nervous system activity.

This distribution view helps identify the concentration of employees experiencing elevated physiological stress, monitor shifts in stress exposure over time, and assess whether recovery strategies and workload management are effectively supporting workforce wellbeing.`,
  weeklyPatterns: `Compares average stress levels and heart rate variability (HRV) trends across the week, highlighting how physiological load and autonomic recovery fluctuate day by day.

This view helps identify recurring high-stress periods, recovery dips, and differences between weekdays and weekends, supporting better workload planning and recovery optimisation.`,
};

export const OverallStressAndMind: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);
  const [trendView, setTrendView] = useState('stress_score');

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId],
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      stress_score: {
        min: typeof metric_min.stress_score === 'number' ? metric_min.stress_score : undefined,
        max: typeof metric_max.stress_score === 'number' ? metric_max.stress_score : undefined,
      },
      hrv: {
        min: typeof metric_min.hrv === 'number' ? metric_min.hrv : undefined,
        max: typeof metric_max.hrv === 'number' ? metric_max.hrv : undefined,
      },
      stress_recovery_balance: {
        min: typeof metric_min.stress_recovery_balance === 'number' ? metric_min.stress_recovery_balance : undefined,
        max: typeof metric_max.stress_recovery_balance === 'number' ? metric_max.stress_recovery_balance : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  // Comparison mode: render one column/line per selected filter value.
  const comparison = useComparisonMode(filterState);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filterState,
    dateRange,
    effectiveClientId,
    comparison,
  );

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Overall Physiological Stress',
    groups,
    isComparing,
    dimension: comparison.dimension,
    filterParams,
    metricSpecs: [
      { label: 'Stress score', keys: ['stress_score'] },
      { label: 'HRV', keys: ['hrv'] },
      { label: 'High-stress days', keys: ['high_stress_days'] },
      { label: 'Stress-recovery balance', keys: ['stress_recovery_balance'] },
    ],
  });

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  // Trends
  const trendsQuery = useOverallStressTrends({
    ...filterParams,
    metrics: ['stress_score', 'stress_recovery_balance', 'hrv'],
    granularity,
  });

  // Patterns - We need to fetch both to combine them
  const stressPatternsQuery = useOverallStressPatterns({
    ...filterParams,
    metric: 'stress_score_patterns',
    granularity: 'weekly', // Assuming weekly patterns for the bottom chart
  });

  const hrvPatternsQuery = useOverallStressPatterns({
    ...filterParams,
    metric: 'hrv_patterns',
    granularity: 'weekly',
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  // Transform Trends Data
  const trendsData = useMemo(() => {
    return transformTrendsData(trendsQuery.data, ['stress_score', 'stress_recovery_balance', 'hrv']);
  }, [trendsQuery.data]);

  // ── Comparison overlays ──────────────────────────────────────────────────
  const comparisonTrends = useComparisonTrends(groups, ['stress_score', 'stress_recovery_balance', 'hrv'], granularity);
  const comparisonStressPatterns = useComparisonPatterns(groups, 'stress_score_patterns', 'weekly');

  const trendCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonTrends.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, [trendView]),
        })),
        trendView,
        'label',
      ),
    [comparisonTrends, trendView],
  );
  const trendCompareLoading = comparisonTrends.some((e) => e.isLoading);

  const stressPatternCompareChart = useMemo(
    () =>
      buildComparisonPatternSeries(
        comparisonStressPatterns.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: e.data?.data?.patterns ?? [],
        })),
        'day',
      ),
    [comparisonStressPatterns],
  );
  const stressPatternCompareLoading = comparisonStressPatterns.some((e) => e.isLoading);

  // Transform Patterns Data
  // Merging stress and hrv patterns by day
  const weeklyPatternsData = useMemo(() => {
    const stress = stressPatternsQuery.data?.data?.patterns || [];
    const hrv = hrvPatternsQuery.data?.data?.patterns || [];

    // Create a map by day
    const combined = new Map<string, { day: string; stressScore?: number; hrv?: number }>();

    stress.forEach((p) => {
      const key = p.day || p.time || '';
      if (key) {
        combined.set(key, { day: key, stressScore: p.value ?? undefined });
      }
    });

    hrv.forEach((p) => {
      const key = p.day || p.time || '';
      if (key) {
        const existing = combined.get(key) || { day: key };
        combined.set(key, { ...existing, hrv: p.value ?? undefined });
      }
    });

    return Array.from(combined.values());
  }, [stressPatternsQuery.data, hrvPatternsQuery.data]);

  const getTrendLabel = () => {
    switch (trendView) {
      case 'stress_score':
        return 'Stress Score';
      case 'hrv':
        return 'Heart Rate Variability';
      case 'stress_recovery_balance':
        return 'Stress-Recovery Balance';
      default:
        return 'Metric';
    }
  };

  const getTrendColor = () => {
    switch (trendView) {
      case 'stress_score':
        return theme.palette.warning.main;
      case 'hrv':
        return theme.palette.info.main;
      case 'stress_recovery_balance':
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case 'stress_score':
        return 5;
      case 'hrv':
        return 100; // Adjust based on expected range
      case 'stress_recovery_balance':
        return 100;
      default:
        return 100;
    }
  };

  const isLoading = trendsQuery.isLoading || stressPatternsQuery.isLoading || hrvPatternsQuery.isLoading;

  const hasError = trendsQuery.isError || stressPatternsQuery.isError || hrvPatternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ComparisonToggle comparison={comparison} />
            <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
            <DurationSelector value={dateRange} onChange={handleDateRangeChange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && <Alert severity='error'>Failed to load data. Please try again later.</Alert>}

        {/* Stat cards + stress distribution, one column per comparison group. */}
        <ComparisonColumns groups={groups} overflowCount={overflowCount}>
          {(group) => (
            <StressStatsColumn
              filterParams={group.filterParams}
              isCentralAdmin={isCentralAdmin}
              isAnonymised={isAnonymised}
              stacked={isComparing}
            />
          )}
        </ComparisonColumns>

        <DashboardCard title='AI Summary' tooltip='AI narrative of the metrics shown.' expandable={false}>
          <AiSummaryPanel
            title={isComparing ? 'Compare these groups with AI' : 'Summarise this view with AI'}
            buildPayload={buildAiPayload}
            disabled={aiDisabled}
          />
        </DashboardCard>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Stress & Recovery Trends'
              subtitle='Daily variation of stress and recovery metrics.'
              metricKey={trendView}
              details={OVERALL_STRESS_AND_MIND_DETAILS.stressAndRecoveryTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setTrendView(newValue);
                  }}
                  size='small'
                >
                  <ToggleButton value='stress_score'>Stress Score</ToggleButton>
                  <ToggleButton value='hrv'>HRV (Stress Indicator)</ToggleButton>
                  <ToggleButton value='stress_recovery_balance'>Stress-Recovery Balance</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {(isComparing ? trendCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  trendCompareChart.data.length > 0 ? (
                    <LineChart
                      data={trendCompareChart.data}
                      xAxisKey='label'
                      series={trendCompareChart.series}
                      height={300}
                      yAxisMin={0}
                      yAxisMax={getYAxisMax()}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No trend data available</Alert>
                    </Box>
                  )
                ) : trendsData.length > 0 ? (
                  <LineChart
                    data={trendsData}
                    xAxisKey='label'
                    series={[
                      {
                        dataKey: trendView,
                        label: getTrendLabel(),
                        color: getTrendColor(),
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[trendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[trendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>No trend data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        {/* Stress Level Distribution now renders inside each comparison column
            (StressStatsColumn). Weekly Patterns overlays one stress line per
            group across the week in comparison mode. */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Weekly Patterns'
              subtitle='Comparison of average stress and HRV trends throughout the week.'
              details={OVERALL_STRESS_AND_MIND_DETAILS.weeklyPatterns}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {(isComparing ? stressPatternCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  stressPatternCompareChart.data.length > 0 ? (
                    <LineChart
                      data={stressPatternCompareChart.data}
                      xAxisKey='day'
                      series={stressPatternCompareChart.series}
                      height={300}
                      yAxisMin={0}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No pattern data available</Alert>
                    </Box>
                  )
                ) : weeklyPatternsData.length > 0 ? (
                  <CombinedBarLineChart
                    data={weeklyPatternsData}
                    xAxisKey='day'
                    barSeries={[
                      {
                        dataKey: 'stressScore',
                        label: 'Stress Score',
                        color: theme.palette.warning.main,
                      },
                    ]}
                    lineSeries={[
                      {
                        dataKey: 'hrv',
                        label: 'HRV (ms)',
                        color: theme.palette.info.main,
                      },
                    ]}
                    height={300}
                    barYAxisMin={0}
                    barYAxisMax={5}
                    lineYAxisMin={0}
                    lineYAxisMax={100}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
